import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { encryptText, encryptAppId } from '../utils/encryption';

const ApplicationCountdown = ({ 
  appId, 
  orgId, 
  labTypeId = '3', 
  collapsed = false,
  onDataLoaded = () => {},
  showDetails = true 
}) => {
  const [countdownData, setCountdownData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Fetch countdown data
  const fetchCountdownData = useCallback(async () => {
    try {
      if (!appId) {
        setError('No application ID provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Build query string with encrypted parameters
      const queryParams = new URLSearchParams();
      if (orgId) {
        const encryptedOrgId = encryptText(orgId);
        queryParams.append('orgId', encryptedOrgId);
      }
      if (labTypeId) {
        const encryptedLabTypeId = encryptText(labTypeId);
        queryParams.append('labTypeId', encryptedLabTypeId);
      }

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api'}/application-countdown/${appId}${queryString ? `?${queryString}` : ''}`;

      console.log('📅 Fetching countdown data from:', url);

      const response = await fetch(url);
      const result = await response.json();

      if (result.success) {
        setCountdownData(result.data);
        onDataLoaded(result.data);
        console.log('📅 Countdown data loaded:', result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch countdown data');
      }
    } catch (err) {
      console.error('❌ Error fetching countdown data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [appId, orgId, labTypeId, onDataLoaded]);

  // Initial fetch and periodic updates
  useEffect(() => {
    fetchCountdownData();
    
    // Update every minute if not expired
    const interval = setInterval(() => {
      if (countdownData && !countdownData.isExpired) {
        fetchCountdownData();
      }
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [fetchCountdownData, countdownData?.isExpired]);

  // Get status icon based on urgency
  const getStatusIcon = (urgency, status) => {
    if (status === 'Expired') {
      return <ErrorIcon sx={{ fontSize: 18, color: '#f44336' }} />;
    } else if (urgency === 'high') {
      return <WarningIcon sx={{ fontSize: 18, color: '#ff5722' }} />;
    } else if (urgency === 'medium') {
      return <AccessTimeIcon sx={{ fontSize: 18, color: '#ff9800' }} />;
    } else {
      return <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />;
    }
  };

  // Format time remaining for display
  const formatTimeRemaining = (timeBreakdown) => {
    if (timeBreakdown.expired) {
      return timeBreakdown.formatted;
    }
    
    if (collapsed) {
      return timeBreakdown.days > 0 ? `${timeBreakdown.days}d` : 
             timeBreakdown.hours > 0 ? `${timeBreakdown.hours}h` : 
             `${timeBreakdown.minutes}m`;
    }
    
    return timeBreakdown.formatted;
  };

  if (loading) {
    return (
      <Box sx={{ 
        p: collapsed ? 1 : 2, 
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LinearProgress size="small" sx={{ width: '100%', height: 2 }} />
      </Box>
    );
  }

  if (error || !countdownData) {
    return collapsed ? null : (
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Alert severity="warning" size="small">
          <Typography variant="caption">
            {error || 'No countdown data available'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const content = (
    <Box sx={{ 
      p: collapsed ? 1 : 2, 
      borderTop: '1px solid #e0e0e0',
      backgroundColor: countdownData.isExpired ? '#ffebee' : 
                      countdownData.isExpiringSoon ? '#fff3e0' : 
                      'transparent',
      transition: 'all 0.3s ease-in-out'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: collapsed ? 'center' : 'space-between',
        mb: collapsed ? 0 : 1 
      }}>
        {!collapsed ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getStatusIcon(countdownData.urgency, countdownData.status)}
              <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                Application Deadline
              </Typography>
            </Box>
          </>
        ) : (
          <Tooltip title={`${countdownData.message}`} placement="right" arrow>
            {getStatusIcon(countdownData.urgency, countdownData.status)}
          </Tooltip>
        )}
      </Box>

      {/* Main countdown display */}
      {!collapsed && (
        <>
          {/* Time remaining chip */}
          <Box sx={{ mb: 1 }}>
            <Chip
              label={formatTimeRemaining(countdownData.timeBreakdown)}
              size="small"
              sx={{
                backgroundColor: countdownData.statusColor,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          </Box>


          {/* Status message */}
          <Typography variant="caption" color="text.secondary">
            {countdownData.message}
          </Typography>

          {/* Detailed breakdown (collapsible) */}
          {showDetails && (
            <Collapse in={expanded} timeout={300}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Application Details:
                </Typography>
                
                <Box sx={{ pl: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    • Registration: {new Date(countdownData.registrationDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    • Expires: {new Date(countdownData.expiryDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    • Lab: {countdownData.laboratoryName || 'N/A'}
                  </Typography>
                  
                  {!countdownData.timeBreakdown.expired && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Time Breakdown:
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                        • {countdownData.timeBreakdown.days} days
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                        • {countdownData.timeBreakdown.hours} hours  
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                        • {countdownData.timeBreakdown.minutes} minutes
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Action alerts */}
          {countdownData.isExpired && (
            <Alert severity="error" size="small" sx={{ mt: 1 }}>
              <Typography variant="caption">
                Application has expired. Please contact support for assistance.
              </Typography>
            </Alert>
          )}
          
          {countdownData.isExpiringSoon && !countdownData.isExpired && (
            <Alert severity="warning" size="small" sx={{ mt: 1 }}>
              <Typography variant="caption">
                Complete your application soon to avoid expiration.
              </Typography>
            </Alert>
          )}
        </>
      )}
    </Box>
  );

  // For collapsed state, wrap in tooltip
  if (collapsed) {
    return (
      <Tooltip 
        title={
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              Application Deadline
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {countdownData.message}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {countdownData.progressPercentage.toFixed(1)}% elapsed
            </Typography>
          </Box>
        } 
        placement="right" 
        arrow
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default ApplicationCountdown;